// Multi-step staff plan upload wizard (UI phase with stub parse).
// Review = individual roster rows; Summary/Save aggregates to the matrix.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  MessageSquareText,
  Plus,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import clsx from 'clsx';
import type { Client, LevelId, Model, StaffPlan } from '@rc/engine/types';
import { api } from '../lib/api';
import { toast } from '../components/Toast';
import { parseGeoclientId, withGeoclientQuery } from '../lib/geoclientContext';
import { fmtFte } from '../lib/format';
import StaffPlanMatrixPreview from '../components/staffPlan/StaffPlanMatrixPreview';
import {
  ROLE_CATEGORIES,
  aggregateImportRowsToCells,
  aggregateImportRowsToOtherRoles,
  clearImportDraft,
  createEmptyDraft,
  emptyImportRow,
  importTotals,
  isMappedFieldMissing,
  isRowClassified,
  loadImportDraft,
  missingMappedFields,
  saveImportDraft,
  stubParseStaffPlan,
  type StaffPlanImportAiConfidence,
  type StaffPlanImportDraft,
  type StaffPlanImportRow,
  type StaffPlanImportStep,
  type StaffPlanRoleCategory,
} from '../lib/staffPlanImport';
import { LEVEL_IDS, STAFF_PLAN_OTHER_ROLE_OPTIONS, exactLevelLabel } from '@rc/engine';
import OtherRolesTable from '../components/staffPlan/OtherRolesTable';

const MAPPED_MISSING_INPUT =
  '!border-amber-400 !bg-amber-50 ring-1 ring-amber-300 focus:!border-amber-500';

function confidenceTone(c?: StaffPlanImportAiConfidence): string {
  if (c === 'low') return 'bg-rose-100 text-rose-800 ring-rose-200';
  if (c === 'medium') return 'bg-amber-100 text-amber-900 ring-amber-200';
  if (c === 'high') return 'bg-emerald-100 text-emerald-800 ring-emerald-200';
  return 'bg-slate-100 text-slate-600 ring-slate-200';
}

const STEPS: { id: StaffPlanImportStep; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'review', label: 'Review' },
  { id: 'summary', label: 'Summary' },
];

export default function StaffPlanImportWizard() {
  const { clientId, planId } = useParams<{ clientId: string; planId: string }>();
  const [search] = useSearchParams();
  const geoclientId = parseGeoclientId(search.get('geoclientId'));
  const fromScenarioId = search.get('from') || undefined;
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [client, setClient] = useState<Client | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [existingPlan, setExistingPlan] = useState<StaffPlan | null>(null);
  const [draft, setDraft] = useState<StaffPlanImportDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isNew = !planId || planId === 'new';

  useEffect(() => {
    if (!clientId || !planId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const c = await api.getClient(clientId);
        let plan: StaffPlan | null = null;
        if (!isNew) {
          try {
            plan = await api.getStaffPlan(clientId, planId);
          } catch {
            plan = null;
          }
        }
        let modelId = plan?.modelId;
        if (!modelId) {
          try {
            const scens = await api.listScenarios(clientId);
            const recent = [...scens].sort((a, b) =>
              (b.updatedAt || '').localeCompare(a.updatedAt || ''),
            )[0];
            modelId = recent?.modelId;
          } catch {
            // fall through
          }
        }
        if (!modelId) {
          const summaries = await api.listModelSummaries();
          modelId = summaries[0]?.id;
        }
        if (!modelId) throw new Error('No model available');
        const m = await api.getModel(modelId);
        if (cancelled) return;
        setClient(c);
        setModel(m);
        setExistingPlan(plan);
        const loaded = loadImportDraft(clientId, planId);
        if (loaded && loaded.modelId === modelId) {
          const stepQ = search.get('step');
          const stepOverride =
            stepQ === 'upload' || stepQ === 'review' || stepQ === 'summary'
              ? stepQ
              : undefined;
          setDraft(
            stepOverride && loaded.rows.length > 0
              ? { ...loaded, step: stepOverride }
              : loaded,
          );
        } else {
          setDraft(createEmptyDraft(clientId, planId, modelId));
        }
      } catch (err) {
        toast('error', (err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, planId, isNew, search]);

  const updateDraft = (
    patch: Partial<StaffPlanImportDraft> | ((d: StaffPlanImportDraft) => StaffPlanImportDraft),
  ) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      saveImportDraft(next);
      return next;
    });
  };

  const totals = useMemo(() => importTotals(draft?.rows ?? []), [draft?.rows]);
  const cells = useMemo(() => aggregateImportRowsToCells(draft?.rows ?? []), [draft?.rows]);
  const otherRoles = useMemo(
    () => aggregateImportRowsToOtherRoles(draft?.rows ?? []),
    [draft?.rows],
  );
  const hasRolesToSave = cells.length > 0 || otherRoles.length > 0;

  const workspaceHref = withGeoclientQuery(
    `/clients/${clientId}/staff-plans/${planId}${fromScenarioId ? `?from=${fromScenarioId}` : ''}`,
    geoclientId,
  );

  const goStep = (step: StaffPlanImportStep) => updateDraft({ step });

  const handleFile = (file: File | null) => {
    if (!file || !model) return;
    if (!/\.xlsx?$/i.test(file.name) && !/\.xls$/i.test(file.name)) {
      toast('error', 'Please choose an Excel file (.xlsx or .xls)');
      return;
    }
    updateDraft({
      fileName: file.name,
      rows: stubParseStaffPlan(model, file.name),
      step: 'review',
      status: 'draft',
    });
    toast('info', 'Loaded stub roster — review each person, then continue to summary.');
  };

  const updateRow = (id: string, patch: Partial<StaffPlanImportRow>) => {
    updateDraft((d) => ({
      ...d,
      rows: d.rows.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        if (next.mappedFte == null && next.sourceFte != null) next.mappedFte = next.sourceFte;
        next.unmapped = !isRowClassified(next);
        return next;
      }),
    }));
  };

  const addRow = () => {
    if (!model) return;
    updateDraft((d) => ({ ...d, rows: [...d.rows, emptyImportRow(model)] }));
  };

  const removeRow = (id: string) => {
    updateDraft((d) => ({ ...d, rows: d.rows.filter((r) => r.id !== id) }));
  };

  const handleSave = async () => {
    if (!client || !model || !draft) return;
    if (!hasRolesToSave) {
      toast('error', 'Include at least one fully classified person before saving.');
      return;
    }
    setSaving(true);
    try {
      const nameBase = draft.fileName
        ? draft.fileName.replace(/\.(xlsx|xls)$/i, '')
        : 'Imported staff plan';
      const now = new Date().toISOString();
      if (isNew || !existingPlan) {
        const plan: StaffPlan = {
          id: crypto.randomUUID(),
          clientId: client.id,
          name: nameBase,
          modelId: model.id,
          kind: 'actual',
          entrySource: 'uploaded',
          notes: draft.fileName
            ? `Imported from ${draft.fileName} (${draft.rows.filter((r) => r.include).length} people)`
            : 'Imported via upload wizard',
          cells,
          otherRoles,
          createdAt: now,
          updatedAt: now,
        };
        const saved = await api.createStaffPlan(client.id, plan, geoclientId);
        clearImportDraft(client.id, planId!);
        saveImportDraft({
          ...draft,
          planId: saved.id,
          status: 'saved',
          step: 'summary',
        });
        toast('success', `Saved staff plan "${saved.name}"`);
        navigate(withGeoclientQuery(`/clients/${client.id}/staff-plans/${saved.id}`, geoclientId), {
          replace: true,
        });
      } else {
        const saved = await api.saveStaffPlan(
          client.id,
          {
            ...existingPlan,
            cells,
            otherRoles,
            kind: 'actual',
            entrySource: 'uploaded',
            notes:
              existingPlan.notes ||
              (draft.fileName ? `Imported from ${draft.fileName}` : undefined),
            updatedAt: now,
          },
          geoclientId,
        );
        updateDraft({ status: 'saved' });
        toast('success', `Updated "${saved.name}" from import`);
        navigate(withGeoclientQuery(`/clients/${client.id}/staff-plans/${saved.id}`, geoclientId));
      }
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !client || !model || !draft) {
    return <div className="p-8 text-sm text-slate-500">Loading import wizard…</div>;
  }

  const step = draft.step;
  const activeChannels = model.channels.filter((c) => c.active !== false);
  const activeDisciplines = model.disciplines.filter((d) => d.active !== false);
  const modelHubs = model.ogsHubs ?? [];

  return (
    <div className="flex flex-col min-h-full">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={workspaceHref}
              className="text-slate-400 hover:text-slate-700 shrink-0"
              title="Back"
            >
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Upload size={16} className="text-brand-purple-500" />
                <h1 className="text-lg font-semibold truncate">Upload staff plan</h1>
                {draft.status === 'draft' && <span className="pill-purple">draft</span>}
                {draft.status === 'saved' && (
                  <span className="pill bg-emerald-50 text-emerald-700">saved</span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">
                Client: <span className="text-slate-700">{client.name}</span>
                {' · '}
                Model: <span className="text-slate-700">{model.name}</span>
                {draft.fileName && (
                  <>
                    {' · '}
                    File: <span className="text-slate-700">{draft.fileName}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="px-8 pb-3 flex gap-2">
          {STEPS.map((s, i) => {
            const active = s.id === step;
            const idx = STEPS.findIndex((x) => x.id === step);
            const done = i < idx;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (s.id === 'upload' || draft.rows.length > 0 || done) goStep(s.id);
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : done
                      ? 'bg-brand-purple-50 text-brand-purple-800 border-brand-purple-200'
                      : 'bg-white text-slate-500 border-slate-200'
                }`}
              >
                {i + 1}. {s.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex-1 px-8 py-6 w-full space-y-4">
        {step === 'upload' && (
          <div className="card max-w-3xl">
            <div className="card-body space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">Upload Excel staff plan</h2>
                <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                  Staff plans usually list individuals. Review classifies each person; Summary
                  aggregates them into the standard matrix.
                </p>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-brand-purple-400 hover:bg-brand-purple-50/30 px-6 py-10 text-center transition-colors"
                onClick={() => fileRef.current?.click()}
              >
                <FileSpreadsheet className="mx-auto text-brand-purple-500 mb-3" size={28} />
                <div className="text-sm font-medium text-slate-800">
                  {draft.fileName ? `Replace file (${draft.fileName})` : 'Choose Excel file'}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  .xlsx or .xls
                </div>
              </button>
              {draft.rows.length > 0 && (
                <div className="flex justify-end">
                  <button type="button" className="btn-primary" onClick={() => goStep('review')}>
                    Continue to review <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 'review' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="People" value={String(totals.people)} />
              <Stat label="Source FTE" value={fmtFte(totals.source)} />
              <Stat label="Mapped included" value={fmtFte(totals.mapped)} accent />
              <Stat
                label="Unmapped FTE"
                value={fmtFte(totals.unmapped)}
                warn={totals.unmapped > 0}
              />
              <Stat label="Matrix cells (preview)" value={String(cells.length)} />
            </div>

            <div className="card overflow-hidden">
              <div className="card-header flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold">Review individuals</h3>
                  <p className="text-xs text-slate-500">
                    Parsed fields, then mapped classification, then AI commentary on the right of
                    each row. Missing mapped fields are amber; low confidence needs rationale in
                    commentary.
                  </p>
                </div>
                <button type="button" className="btn-secondary" onClick={addRow}>
                  <Plus size={14} /> Add person
                </button>
              </div>
              <div className="overflow-auto max-h-[min(70vh,720px)]">
                <table className="w-full min-w-[1760px] border-collapse text-sm table-fixed">
                  <colgroup>
                    <col className="w-10" />
                    <col className="w-9" />
                    <col className="w-[7%]" />
                    <col className="w-[9%]" />
                    <col className="w-[8%]" />
                    <col className="w-[7%]" />
                    <col className="w-[6%]" />
                    <col className="w-14" />
                    <col className="w-14" />
                    <col className="w-[6%]" />
                    <col className="w-28" />
                    <col className="w-[7%]" />
                    <col className="w-[8%]" />
                    <col className="w-[7%]" />
                    <col className="w-14" />
                    <col className="w-14" />
                    <col className="w-[6%]" />
                    <col className="w-[7%]" />
                    <col className="w-[360px]" />
                    <col className="w-10" />
                  </colgroup>
                  <thead>
                    <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-1.5 py-2 text-left bg-white border-b border-slate-200" />
                      <th
                        colSpan={9}
                        className="px-2 py-2 text-left bg-slate-100 border-b border-x border-slate-200 text-slate-600"
                      >
                        Parsed / Detected
                      </th>
                      <th
                        colSpan={8}
                        className="px-2 py-2 text-left bg-brand-purple-50 border-b border-x border-brand-purple-100 text-brand-purple-800"
                      >
                        Mapped
                      </th>
                      <th className="sticky right-10 z-20 px-2 py-2 text-left bg-sky-50 border-b border-x border-sky-100 text-sky-800 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                        AI review
                      </th>
                      <th className="sticky right-0 z-20 px-1.5 py-2 bg-white border-b border-slate-200" />
                    </tr>
                    <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-1.5 py-2 text-center bg-white border-b border-slate-200">Inc</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">#</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">Name</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">Email</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">Title</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">Function</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-slate-200">Channel</th>
                      <th className="px-1.5 py-2 text-right bg-slate-50 border-b border-slate-200">FTE</th>
                      <th className="px-1.5 py-2 text-center bg-slate-50 border-b border-slate-200">OGS</th>
                      <th className="px-1.5 py-2 text-left bg-slate-50 border-b border-r border-slate-200">Hub loc</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-slate-200">Level</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-slate-200">Category</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-slate-200">Discipline</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-slate-200">Channel</th>
                      <th className="px-1.5 py-2 text-right bg-brand-purple-50/50 border-b border-slate-200">FTE</th>
                      <th className="px-1.5 py-2 text-center bg-brand-purple-50/50 border-b border-slate-200">Hub</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-slate-200">Hub loc</th>
                      <th className="px-1.5 py-2 text-left bg-brand-purple-50/50 border-b border-r border-brand-purple-100">Notes</th>
                      <th className="sticky right-10 z-20 px-1.5 py-2 text-left bg-sky-50 border-b border-sky-100 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                        Commentary
                      </th>
                      <th className="sticky right-0 z-20 px-1.5 py-2 bg-white border-b border-slate-200" />
                    </tr>
                  </thead>
                  <tbody>
                    {draft.rows.map((r, idx) => {
                      const classified = isRowClassified(r);
                      const missing = missingMappedFields(r);
                      const needsCommentary =
                        r.aiConfidence === 'low' || !classified || missing.length > 0;
                      return (
                          <tr
                            key={r.id}
                            className={clsx(
                              'border-b border-slate-100',
                              !classified
                                ? 'bg-amber-50/70 border-l-4 border-l-amber-400'
                                : r.aiConfidence === 'low'
                                  ? 'bg-rose-50/40 border-l-4 border-l-rose-300'
                                  : 'bg-white',
                            )}
                          >
                          <td className="px-1.5 py-1.5 align-top text-center">
                            <input
                              type="checkbox"
                              className="align-middle mt-1"
                              checked={r.include}
                              onChange={(e) => updateRow(r.id, { include: e.target.checked })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle text-slate-400 tabular-nums text-xs">
                            {idx + 1}
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.name || ''}
                              onChange={(e) => updateRow(r.id, { name: e.target.value })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.email || ''}
                              onChange={(e) => updateRow(r.id, { email: e.target.value })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.title}
                              title={!classified ? 'Needs mapping' : undefined}
                              onChange={(e) => updateRow(r.id, { title: e.target.value })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.sourceFunction || ''}
                              onChange={(e) => updateRow(r.id, { sourceFunction: e.target.value })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.sourceChannel || ''}
                              onChange={(e) => updateRow(r.id, { sourceChannel: e.target.value })}
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              className="input !px-1.5 !py-1 text-xs text-right tabular-nums"
                              value={r.sourceFte}
                              onChange={(e) =>
                                updateRow(r.id, { sourceFte: Number(e.target.value) || 0 })
                              }
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle text-center text-xs text-slate-600">
                            {r.sourceOgsHubbed ? 'Yes' : 'No'}
                          </td>
                          <td className="px-1.5 py-1.5 align-middle border-r border-slate-100">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.sourceHubLocation || ''}
                              onChange={(e) =>
                                updateRow(r.id, { sourceHubLocation: e.target.value })
                              }
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <select
                              className={clsx(
                                'input !px-1.5 !py-1 text-xs',
                                isMappedFieldMissing(r, 'levelId') && MAPPED_MISSING_INPUT,
                              )}
                              value={r.levelId || ''}
                              title={
                                isMappedFieldMissing(r, 'levelId') ? 'Level required' : undefined
                              }
                              onChange={(e) =>
                                updateRow(r.id, {
                                  levelId: (e.target.value || undefined) as LevelId | undefined,
                                })
                              }
                            >
                              <option value="">—</option>
                              {LEVEL_IDS.map((id) => (
                                <option key={id} value={id}>
                                  {exactLevelLabel(id)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <select
                              className={clsx(
                                'input !px-1.5 !py-1 text-xs',
                                isMappedFieldMissing(r, 'roleCategory') && MAPPED_MISSING_INPUT,
                              )}
                              value={r.roleCategory || ''}
                              title={
                                isMappedFieldMissing(r, 'roleCategory')
                                  ? 'Category required'
                                  : undefined
                              }
                              onChange={(e) => {
                                const roleCategory = (e.target.value || undefined) as
                                  | StaffPlanRoleCategory
                                  | undefined;
                                updateRow(
                                  r.id,
                                  roleCategory === 'other'
                                    ? { roleCategory, disciplineId: undefined, channelId: undefined }
                                    : { roleCategory, otherRole: undefined, channelId: r.channelId || 'all' },
                                );
                              }}
                            >
                              <option value="">—</option>
                              {ROLE_CATEGORIES.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            {r.roleCategory === 'other' ? (
                              <input
                                className={clsx(
                                  'input !px-2 !py-1 text-xs',
                                  isMappedFieldMissing(r, 'disciplineId') && MAPPED_MISSING_INPUT,
                                )}
                                list="staff-plan-other-roles"
                                value={r.otherRole || ''}
                                placeholder="Pick or type a role"
                                title={
                                  isMappedFieldMissing(r, 'disciplineId')
                                    ? 'Role required — pick one or type your own'
                                    : r.otherRole
                                }
                                onChange={(e) => updateRow(r.id, { otherRole: e.target.value })}
                              />
                            ) : (
                              <select
                                className={clsx(
                                  'input !px-1.5 !py-1 text-xs',
                                  isMappedFieldMissing(r, 'disciplineId') && MAPPED_MISSING_INPUT,
                                )}
                                value={r.disciplineId || ''}
                                title={
                                  isMappedFieldMissing(r, 'disciplineId')
                                    ? 'Discipline required'
                                    : undefined
                                }
                                onChange={(e) =>
                                  updateRow(r.id, { disciplineId: e.target.value || undefined })
                                }
                              >
                                <option value="">—</option>
                                {activeDisciplines.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            {r.roleCategory === 'other' ? (
                              <span
                                className="block px-1.5 text-xs text-slate-300"
                                title="Channel doesn't apply to Other roles"
                              >
                                —
                              </span>
                            ) : (
                              <select
                                className="input !px-1.5 !py-1 text-xs"
                                value={r.channelId || 'all'}
                                onChange={(e) => updateRow(r.id, { channelId: e.target.value })}
                              >
                                <option value="all">All</option>
                                {activeChannels.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              className="input !px-1.5 !py-1 text-xs text-right tabular-nums"
                              value={r.mappedFte ?? ''}
                              onChange={(e) =>
                                updateRow(r.id, {
                                  mappedFte:
                                    e.target.value === ''
                                      ? undefined
                                      : Number(e.target.value) || 0,
                                })
                              }
                            />
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <select
                              className="input !px-1.5 !py-1 text-xs"
                              value={r.hub ? 'yes' : 'no'}
                              onChange={(e) => {
                                const hub = e.target.value === 'yes';
                                if (!hub) {
                                  updateRow(r.id, {
                                    hub: false,
                                    hubId: undefined,
                                    hubLocation: undefined,
                                  });
                                } else {
                                  const first = modelHubs[0];
                                  updateRow(r.id, {
                                    hub: true,
                                    hubId: r.hubId || first?.id,
                                    hubLocation:
                                      r.hubLocation ||
                                      modelHubs.find((h) => h.id === (r.hubId || first?.id))
                                        ?.label,
                                  });
                                }
                              }}
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </td>
                          <td className="px-1.5 py-1.5 align-middle">
                            <select
                              className={clsx(
                                'input !px-1.5 !py-1 text-xs',
                                isMappedFieldMissing(r, 'hubId') && MAPPED_MISSING_INPUT,
                              )}
                              value={r.hubId || ''}
                              disabled={modelHubs.length === 0}
                              title={
                                isMappedFieldMissing(r, 'hubId')
                                  ? 'Hub location required when Hub = Yes'
                                  : modelHubs.length === 0
                                    ? 'No hubs defined on this model — add them under Buckets & hubs'
                                    : undefined
                              }
                              onChange={(e) => {
                                const hubId = e.target.value || undefined;
                                const hub = modelHubs.find((h) => h.id === hubId);
                                updateRow(r.id, {
                                  hubId,
                                  hubLocation: hub?.label,
                                  hub: !!hubId,
                                });
                              }}
                            >
                              <option value="">—</option>
                              {modelHubs.map((h) => (
                                <option key={h.id} value={h.id}>
                                  {h.label} ({h.shore})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1.5 py-1.5 align-middle border-r border-brand-purple-50">
                            <input
                              className="input !px-2 !py-1 text-xs"
                              value={r.notes || ''}
                              onChange={(e) => updateRow(r.id, { notes: e.target.value })}
                              placeholder="Notes"
                            />
                          </td>
                          <td
                            className={clsx(
                              'sticky right-10 z-10 px-1.5 py-1.5 align-middle border-l shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)]',
                              needsCommentary
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-sky-50 border-sky-100',
                            )}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span
                                className={clsx(
                                  'shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded ring-1',
                                  confidenceTone(r.aiConfidence),
                                )}
                                title={
                                  missing.length > 0
                                    ? `Missing: ${missing
                                        .map((f) =>
                                          f === 'levelId'
                                            ? 'Level'
                                            : f === 'roleCategory'
                                              ? 'Category'
                                              : f === 'disciplineId'
                                                ? 'Discipline'
                                                : 'Hub loc',
                                        )
                                        .join(', ')}`
                                    : r.aiComment || undefined
                                }
                              >
                                {r.aiConfidence || '—'}
                              </span>
                              <MessageSquareText
                                size={12}
                                className={clsx(
                                  'shrink-0',
                                  needsCommentary ? 'text-amber-600' : 'text-sky-600',
                                )}
                              />
                              <input
                                type="text"
                                className={clsx(
                                  'input !px-1.5 !py-1 text-[11px] flex-1 min-w-0',
                                  needsCommentary && !r.aiComment && MAPPED_MISSING_INPUT,
                                )}
                                value={r.aiComment || ''}
                                title={r.aiComment || undefined}
                                placeholder={
                                  needsCommentary
                                    ? 'Why uncertain / incomplete…'
                                    : 'Mapping rationale…'
                                }
                                onChange={(e) =>
                                  updateRow(r.id, { aiComment: e.target.value })
                                }
                              />
                            </div>
                          </td>
                          <td className="sticky right-0 z-10 px-1.5 py-1.5 align-middle text-center bg-white">
                            <button
                              type="button"
                              className="inline-flex text-slate-400 hover:text-rose-600"
                              onClick={() => removeRow(r.id)}
                              title="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                          </tr>
                      );
                    })}
                  </tbody>
                </table>
                <datalist id="staff-plan-other-roles">
                  {STAFF_PLAN_OTHER_ROLE_OPTIONS.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="flex justify-between">
              <button type="button" className="btn-secondary" onClick={() => goStep('upload')}>
                <ArrowLeft size={14} /> Back
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => goStep('summary')}
                disabled={!hasRolesToSave}
              >
                Continue to summary <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === 'summary' && (
          <>
            <div className="card">
              <div className="card-header">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">
                    Aggregated staff matrix
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Included classified people rolled up to discipline × channel × level (On /
                    Hub). Same layout as the staff plan grid — saving writes this into the plan.
                    Other roles are saved alongside it and counted in totals, but not calculated.
                  </p>
                </div>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat
                    label="People included"
                    value={String(
                      draft.rows.filter((r) => r.include && isRowClassified(r)).length,
                    )}
                  />
                  <Stat label="Matrix cells" value={String(cells.length)} />
                  <Stat
                    label="Total FTE"
                    value={fmtFte(
                      [...cells, ...otherRoles].reduce((s, c) => s + c.fte, 0),
                    )}
                    accent
                  />
                  <Stat
                    label="Disciplines"
                    value={String(new Set(cells.map((c) => c.disciplineId)).size)}
                  />
                </div>
                <StaffPlanMatrixPreview model={model} cells={cells} />
                {otherRoles.length > 0 && <OtherRolesTable roles={otherRoles} />}
              </div>
            </div>
            <div className="flex justify-between">
              <button type="button" className="btn-secondary" onClick={() => goStep('review')}>
                <ArrowLeft size={14} /> Back to review
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || !hasRolesToSave}
              >
                <Save size={14} />{' '}
                {saving ? 'Saving…' : isNew ? 'Save staff plan' : 'Update staff plan'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  warn,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        warn
          ? 'border-amber-200 bg-amber-50'
          : accent
            ? 'border-brand-purple-200 bg-brand-purple-50/50'
            : 'border-slate-200 bg-white'
      }`}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
